import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { handleAdminCreateBlog, handleAdminGetBlogDetail, handleAdminUpdateBlog } from '../../services/APIClient';

// Quill toolbar configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'align': [] }],
    ['link', 'image', 'video'],
    ['blockquote', 'code-block'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'indent', 'direction', 'align',
  'link', 'image', 'video',
  'blockquote', 'code-block'
];

const AddBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'draft',
    category: '',
    tags: [],
    metaTitle: '',
    metaDescription: '',
    featuredImage: '' // URL or filename from server
  });
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (id) {
      const fetchBlogDetail = async () => {
        try {
          setLoading(true);
          const response = await handleAdminGetBlogDetail(id);
          console.log("Blog detail response:", response);
          if (response?.code === 200) {
            const blog = response.data.blog;
            setFormData({
              title: blog.title || '',
              content: blog.content || '',
              excerpt: blog.excerpt || '',
              status: blog.status || 'draft',
              category: blog.category || '',
              tags: blog.tags || [],
              metaTitle: blog.metaTitle || blog.meta?.title || '',
              metaDescription: blog.metaDescription || blog.meta?.description || '',
              featuredImage: blog.featuredImage || ''
            });
            if (blog.featuredImage) {
              setImagePreview(blog.featuredImage);
            }
          }
        } catch (error) {
          console.error("Error fetching blog details", error);
        } finally {
          setLoading(false);
        }
      };
      fetchBlogDetail();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let response;
      // Use FormData so we can upload image file if provided
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('content', formData.content);
      payload.append('excerpt', formData.excerpt);
      payload.append('status', formData.status);
      payload.append('category', formData.category);
      payload.append('metaTitle', formData.metaTitle || '');
      payload.append('metaDescription', formData.metaDescription || '');
      // tags as JSON
      payload.append('tags', JSON.stringify(formData.tags || []));
      if (imageFile) {
        payload.append('featuredImage', imageFile);
      }

      if (id) {
        response = await handleAdminUpdateBlog(id, payload);
      } else {
        response = await handleAdminCreateBlog(payload);
      }
      if (response?.code === 200) {
        alert(`Blog ${id ? 'updated' : 'created'} successfully!`);
        navigate('/blogs');
      } else {
        alert(`Failed to ${id ? 'update' : 'create'} blog`);
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      // clear featuredImage url since we're using a new file
      setFormData(prev => ({ ...prev, featuredImage: '' }));
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <div className="p-6">
      {/* Header - stacked on small screens */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{id ? 'Edit Blog' : 'Create New Blog'}</h1>
          <p className="text-gray-600 mt-1">{id ? 'Update your blog post' : 'Write and publish your blog post'}</p>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={() => navigate('/blogs')}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to Blogs
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit}>
          {/* Main Form Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            {/* Title */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Blog Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Enter a catchy title"
                required
              />
            </div>

            {/* Excerpt */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Short Description
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief summary (shown in blog listings)"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum 200 characters recommended</p>
            </div>

            {/* Content */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content *
              </label>
              <div className="quill-wrapper">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your amazing content here..."
                  style={{ minHeight: '300px' }}
                />
              </div>
              <style>{`
                .quill-wrapper .ql-container {
                  min-height: 250px;
                  font-size: 16px;
                }
                .quill-wrapper .ql-editor {
                  min-height: 250px;
                }
                .quill-wrapper .ql-toolbar {
                  border-top-left-radius: 8px;
                  border-top-right-radius: 8px;
                  background: #f9fafb;
                }
                .quill-wrapper .ql-container {
                  border-bottom-left-radius: 8px;
                  border-bottom-right-radius: 8px;
                }
              `}</style>
            </div>

            {/* Category & Tags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Category</option>
                  <option value="Technology">Technology</option>
                  <option value="Business">Business</option>
                  <option value="Health">Health</option>
                  <option value="Education">Education</option>
                  <option value="Lifestyle">Lifestyle</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Add tag and press Enter"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Meta Title & Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="SEO meta title"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="SEO meta description"
              />
            </div>

            {/* Image upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={handleFileChange} />
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="w-28 h-16 sm:w-32 sm:h-20 md:w-40 md:h-24 object-cover rounded-md" />
                ) : formData.featuredImage ? (
                  <img src={formData.featuredImage} alt="featured" className="w-28 h-16 sm:w-32 sm:h-20 md:w-40 md:h-24 object-cover rounded-md" />
                ) : null}
              </div>
            </div>

            {/* Status */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Status
              </label>
              <div className="flex gap-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="draft"
                    checked={formData.status === 'draft'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-2">Save as Draft</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="published"
                    checked={formData.status === 'published'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-2">Publish Now</span>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div className="text-sm text-gray-500">
                Fill all required fields (*) before saving
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => navigate('/blogs')}
                  className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{backgroundColor: loading ? 'gray' : '#333333'}}
                  className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Save Blog
                </button>
              </div>
            </div>
          </div>
        </form>

  {/* Tips Card */}
  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Writing Tips
          </h3>
          <ul className="text-sm text-blue-700 space-y-2">
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Keep titles under 60 characters for better SEO</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Use short paragraphs for better readability</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Add relevant tags to help readers find your content</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Preview before publishing</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AddBlog;
